import { Component, Prop, Vue, Watch, Emit } from 'vue-property-decorator';
import RecapService from '@/service/recap.service';

interface Podcast {
  id: number;
  requestId: string;
  userId: string;
  recapId: string;
  title: string;
  url: string;
  speakerId: string;
  role: string;
  loadingData: boolean;
  image: string;
  hostName: string;
  guestName: string;
  publishedAt: string;
  createdAt: string;
}

@Component
export default class PodcastLoading extends Vue {
  @Prop({
    type: Object as () => Podcast,
    required: false,
    default: null,
  })
  readonly podcast!: Podcast | null;

  @Prop({ type: String, required: true })
  readonly recapTitle!: string;

  @Prop({ type: Boolean, required: false, default: false })
  readonly isHideSetting!: boolean;
  dataPodcast: Podcast | null = null;
  recapService: RecapService = new RecapService();
  responseRecapByDay: any = null;
  userId = '1';
  podCastData: any = null;
  isDarkMode = false;

  podcastEnabled = true;
  isPlaying = false;
  isMuted = false;
  playerProgress = 0;
  volume = 0;
  currentTime = 0;
  totalTime = 0;
  audio: HTMLAudioElement = new Audio('');
  showLoading = false;
  downloadStatus = false;

  @Watch('podcast', { immediate: true, deep: true })
  onPodcastChanged(newVal: Podcast | null) {
    if (newVal) {
      this.dataPodcast = newVal;
      if (newVal.url) {
        this.showLoading = true;
        this.loadAudio(newVal.url);
      } else {
        this.showLoading = false;
      }
    }
  }

  mounted() {
    this.initializeUserData();
    document.addEventListener('click', this.handleOutsideClick);

    if (this.podcast?.url) {
      this.showLoading = true;
      this.loadAudio(this.podcast.url);
    } else {
      this.showLoading = false;
    }
  }

  beforeUnmount() {
    this.stopAudio();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private initializeUserData(): void {
    const userData = this.getLocalStorageUser();
    if (userData) {
      this.userId = userData.id;
      this.isDarkMode = userData.colorTheme === 'dark';
    }
  }

  private getLocalStorageUser(): any {
    try {
      const userData = localStorage.getItem('localStorageUser');
      return userData ? JSON.parse(userData) : null;
    } catch (e) {
      console.error('Error parsing user data:', e);
      return null;
    }
  }

  private stopAudio() {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.isPlaying = false;
  }

  private handleOutsideClick(event: MouseEvent) {
    const popup = this.$el as HTMLElement;
    if (!popup.contains(event.target as Node)) {
      this.stopAudio();
    }
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  togglePlay(): void {
    if (!this.podcastEnabled) return;

    if (this.isPlaying) {
      this.audio.pause();
    } else {
      this.audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
    this.isPlaying = !this.isPlaying;
  }

  resetAudio(): void {
    this.audio.currentTime = 0;
    this.isPlaying = false;
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.audio.muted = this.isMuted;
  }

  onProgressChange(): void {
    if (!this.podcastEnabled) return;
    this.audio.currentTime = (this.playerProgress / 100) * this.totalTime;
  }

  updateProgress(): void {
    this.playerProgress = (this.audio.currentTime / this.totalTime) * 100;
    this.currentTime = this.audio.currentTime;
  }

  loadAudio(url: string): void {
    this.audio.src = url;
    this.audio.load();
    this.audio.onloadedmetadata = () => {
      this.totalTime = this.audio.duration;
    };
    this.audio.ontimeupdate = this.updateProgress.bind(this);
    this.audio.onended = () => {
      this.isPlaying = false;
    };
  }

  async copyPodcastUrl() {
    if (!this.dataPodcast?.url) return;

    try {
      await navigator.clipboard.writeText(this.dataPodcast.url);
      this.showToast(this.$t('briefing.recap.toast') as string, this.$t('briefing.recap.success') as string, 'success');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = this.dataPodcast.url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  async downloadPodcast(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.dataPodcast?.url || this.downloadStatus) return;

    this.downloadStatus = true;

    try {
      const response = await fetch(this.dataPodcast.url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.dataPodcast.title || 'podcast'}.mp3`;

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      this.downloadStatus = false;
    }
  }

  private showToast(message: string, title: string, variant = 'info'): void {
    this.$bvToast.toast(message, {
      title: title,
      variant: variant,
      solid: true,
      autoHideDelay: 5000,
      toaster: 'b-toaster-bottom-right',
      noCloseButton: false,
      appendToast: true,
    });
  }

  @Emit('go-back')
  goBackSetting() {
    this.stopAudio();
    return true;
  }
}
